import React, { useEffect, useMemo, useState } from 'react';
import { FaCamera, FaCheckCircle, FaMapMarkerAlt, FaPhoneAlt, FaRegFileAlt, FaTimes } from 'react-icons/fa';

const initialForm = {
  direccion: '',
  detalle: '',
  telefono: '',
  foto: null,
  website: ''
};

const MAX_PHOTO_BYTES = 700 * 1024;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('No se pudo leer la foto'));
    reader.readAsDataURL(file);
  });
}

function Reporte() {
  const [form, setForm] = useState(initialForm);
  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [reportId, setReportId] = useState('');

  const previewUrl = useMemo(() => {
    if (!form.foto) return '';
    return URL.createObjectURL(form.foto);
  }, [form.foto]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    setSent(false);
    setError('');
    setReportId('');

    if (name === 'foto') {
      const file = files?.[0] || null;
      if (file && file.size > MAX_PHOTO_BYTES) {
        setError('La foto no debe superar 700 KB.');
        event.target.value = '';
        return;
      }

      setForm((current) => ({ ...current, foto: file }));
      return;
    }

    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSent(false);
    setError('');
    setReportId('');
    setIsSubmitting(true);

    try {
      const fotoDataUrl = form.foto ? await fileToDataUrl(form.foto) : '';
      const response = await fetch('/api/reportes-ciudadanos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          direccion: form.direccion,
          detalle: form.detalle,
          telefono: form.telefono,
          fotoDataUrl,
          website: form.website
        })
      });

      if (response.status === 204) {
        setSent(true);
        setForm(initialForm);
        return;
      }

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'No se pudo enviar el reporte.');
      }

      setReportId(result.data?.id || '');
      setSent(true);
      setForm(initialForm);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const removePhoto = () => {
    setForm((current) => ({ ...current, foto: null }));
  };

  return (
    <main className="min-h-screen bg-slate-950 text-slate-100">
      <section className="mx-auto flex min-h-screen w-full max-w-5xl items-center px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid w-full gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-500/10 px-3 py-1 text-sm font-semibold text-orange-200">
              <FaRegFileAlt className="h-3.5 w-3.5" />
              Consorcio Parqueo Amigo
            </div>
            <div>
              <h1 className="text-3xl font-bold leading-tight text-white sm:text-4xl">
                Deja tu queja o reporte
              </h1>
              <p className="mt-3 max-w-md text-sm leading-6 text-slate-300 sm:text-base">
                Comparte los datos del suceso para que podamos revisar tu caso y darte contestacion.
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-white/10 bg-white p-4 text-slate-900 shadow-2xl sm:p-6"
          >
            {sent && (
              <div className="mb-4 flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <FaCheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Reporte recibido correctamente{reportId ? `: ${reportId}` : ''}.</span>
              </div>
            )}

            {error && (
              <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={handleChange}
                className="hidden"
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
              />

              <label className="block">
                <span className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <FaMapMarkerAlt className="text-orange-600" />
                  Direccion
                </span>
                <input
                  name="direccion"
                  value={form.direccion}
                  onChange={handleChange}
                  required
                  autoComplete="street-address"
                  className="w-full rounded-md border border-slate-300 px-3 py-3 text-base outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                  placeholder="Ejemplo: Av. Quito y Tsachila"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <FaRegFileAlt className="text-orange-600" />
                  Detalle del suceso
                </span>
                <textarea
                  name="detalle"
                  value={form.detalle}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full resize-none rounded-md border border-slate-300 px-3 py-3 text-base outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                  placeholder="Describe lo ocurrido"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <FaPhoneAlt className="text-orange-600" />
                  Datos telefonicos para dar contestacion
                </span>
                <input
                  name="telefono"
                  value={form.telefono}
                  onChange={handleChange}
                  required
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  className="w-full rounded-md border border-slate-300 px-3 py-3 text-base outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                  placeholder="Telefono o WhatsApp"
                />
              </label>

              <div>
                <span className="mb-1.5 flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <FaCamera className="text-orange-600" />
                  Foto opcional
                </span>
                <label className="flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center transition hover:border-orange-400 hover:bg-orange-50">
                  <input
                    name="foto"
                    type="file"
                    accept="image/*"
                    onChange={handleChange}
                  className="sr-only"
                    disabled={isSubmitting}
                  />
                  <FaCamera className="mb-2 h-6 w-6 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Seleccionar imagen</span>
                  <span className="mt-1 text-xs text-slate-500">JPG, PNG o foto desde el celular</span>
                </label>
              </div>

              {form.foto && (
                <div className="relative overflow-hidden rounded-md border border-slate-200">
                  <img src={previewUrl} alt="Vista previa" className="h-44 w-full object-cover sm:h-56" />
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-black"
                    aria-label="Quitar foto"
                  >
                    <FaTimes />
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-orange-600 px-4 py-3 text-base font-bold text-white transition hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <FaCheckCircle />
                {isSubmitting ? 'Enviando...' : 'Enviar reporte'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}

export default Reporte;
